import { HttpError } from '../../lib/errors';
import Error5xx from '../../lib/screens/error-5xx';
import Error403 from '../../lib/screens/error-403';
import Error400 from '../../lib/screens/error-400';
import Error401Modal from '../../lib/components/error-401-modal';
import HttpStatusCodes from 'http-status-codes';
import { captureException } from '@frontend/feature-monitor';
import { ErrorBoundary } from 'react-error-boundary';
import PropTypes from 'prop-types';
import { createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

const HelpUrlContext = createContext(undefined);

/**
 * Returns the appropriate error component based on the HTTP status code.
 * @param {number} statusCode - The HTTP status code of the error.
 * @param {Error} error - The error object.
 * @param {string} helpUrl optional - The URL to the help center.
 * @returns {React.Component} - The corresponding React component for the error.
 */
function getErrorHttpComponent(statusCode, error, helpUrl) {
  if (statusCode >= 500 && statusCode <= 599) {
    captureException(error);
  }
  switch (statusCode) {
    case HttpStatusCodes.UNAUTHORIZED:
      return <Error401Modal open={true} />
    case HttpStatusCodes.FORBIDDEN:
      return <Error403 />
    case HttpStatusCodes.BAD_REQUEST:
      return <Error400 />
    default:
      return <Error5xx helpUrl={helpUrl} />;
  }
}

function ErrorFallback({ error }) {
  const helpUrl = useContext(HelpUrlContext);
  if (error?.status && error instanceof HttpError) {
    return getErrorHttpComponent(error.status, error, helpUrl);
  }
  captureException(error);
  return <Error5xx helpUrl={helpUrl} />;
}

ErrorFallback.propTypes = {
  error: PropTypes.instanceOf(Error),
};

const ErrorBoundaryWrapper = ({ children, enableErrorBoundary, helpUrl }) => {
  const location = useLocation();
  const inheritedHelpUrl = useContext(HelpUrlContext);
  const effectiveHelpUrl = helpUrl ?? inheritedHelpUrl;

  const boundary = enableErrorBoundary ? (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      resetKeys={[location.pathname]}
    >
      {children}
    </ErrorBoundary>
  ) : children;

  return (
    <HelpUrlContext.Provider value={effectiveHelpUrl}>
      {boundary}
    </HelpUrlContext.Provider>
  );
};

ErrorBoundaryWrapper.propTypes = {
  children: PropTypes.element,
  enableErrorBoundary: PropTypes.bool,
  helpUrl: PropTypes.string,
};

export default ErrorBoundaryWrapper;
